import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Completa todos los campos.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("¡Mensaje enviado! Te responderé pronto.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar el mensaje.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      className="relative py-24 md:py-32 px-6 md:px-12"
      data-testid="contact-section"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        {/* Left side: title + socials */}
        <div className="md:col-span-5">
          <div className="text-xs tracking-[0.3em] uppercase text-[#F25C32] mb-6">— Contacto</div>
          <h2 className="heading-display text-5xl sm:text-6xl md:text-6xl lg:text-7xl text-[#F5F5F0] leading-[0.9] mb-10 break-words">
            Hablemos
          </h2>
          <p className="text-[#9CA3AF] text-base md:text-lg leading-relaxed max-w-md mb-10">
            ¿Tienes un proyecto en mente? Cuéntame los detalles y juntos le daremos voz a tu historia.
          </p>

          <div className="space-y-4 mb-10">
            <a
              href="mailto:hola@santiagovo.com"
              className="flex items-center gap-4 text-[#F5F5F0] hover:text-[#F25C32] transition-colors"
              data-testid="contact-email-link"
            >
              <span className="w-10 h-10 rounded-full bg-[#F25C32]/15 text-[#F25C32] flex items-center justify-center">
                <i className="fa-solid fa-envelope" />
              </span>
              <span className="text-base md:text-lg">hola@santiagovo.com</span>
            </a>
          </div>

          <div className="flex items-center gap-5">
            <a
              href="https://www.instagram.com/santiagovoice/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-[#F25C32]/15 text-[#F25C32] flex items-center justify-center hover:bg-[#F25C32] hover:text-white transition-colors"
              data-testid="social-instagram"
              aria-label="Instagram"
            >
              <i className="fa-brands fa-instagram text-lg" />
            </a>
            <a
              href="https://www.linkedin.com/in/stgoramirez/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-[#F25C32]/15 text-[#F25C32] flex items-center justify-center hover:bg-[#F25C32] hover:text-white transition-colors"
              data-testid="social-linkedin"
              aria-label="LinkedIn"
            >
              <i className="fa-brands fa-linkedin-in text-lg" />
            </a>
            <a
              href="mailto:hola@santiagovo.com"
              className="w-12 h-12 rounded-full bg-[#F25C32]/15 text-[#F25C32] flex items-center justify-center hover:bg-[#F25C32] hover:text-white transition-colors"
              data-testid="social-email"
              aria-label="Email"
            >
              <i className="fa-solid fa-envelope text-lg" />
            </a>
          </div>
        </div>

        {/* Right side: form */}
        <form onSubmit={onSubmit} className="md:col-span-7 space-y-2" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF]">Nombre</label>
              <input
                value={form.name}
                onChange={onChange("name")}
                className="editorial-input"
                placeholder="Tu nombre"
                data-testid="contact-input-name"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF]">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className="editorial-input"
                placeholder="tu@email.com"
                data-testid="contact-input-email"
              />
            </div>
          </div>

          <div className="pt-6">
            <label className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF]">Asunto</label>
            <input
              value={form.subject}
              onChange={onChange("subject")}
              className="editorial-input"
              placeholder="¿Sobre qué quieres hablar?"
              data-testid="contact-input-subject"
            />
          </div>

          <div className="pt-6">
            <label className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF]">Mensaje</label>
            <textarea
              value={form.message}
              onChange={onChange("message")}
              className="editorial-input resize-none"
              rows={5}
              placeholder="Cuéntame sobre tu proyecto…"
              data-testid="contact-input-message"
            />
          </div>

          <div className="pt-8">
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#F25C32] hover:bg-[#D94E28] text-white rounded-full px-10 py-4 uppercase tracking-[0.25em] text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-3"
              data-testid="contact-form-submit"
            >
              {submitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" />
                  Enviando…
                </>
              ) : (
                <>
                  Enviar mensaje
                  <i className="fa-solid fa-arrow-right" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
